import { useEffect, useState } from 'react';
import type { ComponentProps } from 'react';
import { Box, FormHelperText, styled } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import type { Control, FieldValues } from 'react-hook-form';
import {
  Controller,
  useFormContext,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import type { ReservationFormInput } from '../schema/reservationForm.schema';
import { useAppSelector } from '@/hooks/useAppSelector';
import { usePortalLabel } from '@/hooks/usePortalLabel';
import * as lookupSlice from '@/data/lookup.slice';
import {
  type TripArrayFieldKey,
  useReservationFormMachines,
} from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../hooks/useFieldActorSnapshot';
import type { TripLegEstimatedPickupContext } from '../machines/tripLegEstimatedPickup.machine';
import { isPickupTimeFarFromAppointment } from '../utils/isPickupTimeFarFromAppointment';
import reservationUtils from '@/services/reservationUtils';
import {
  defaultTimePickerReferenceDate,
  inZoneKeepingLocalTime,
  isFiniteOrZero,
  isPickupLeadTimeTooShort,
  isValidDateTime,
  isWithinPickupDateRange,
  mergeDatePart,
  mergeTimePart,
  safeMinus,
  toPickerDateTime,
} from '../utils/pickerDateTime';

const PREFIX = 'TripArrayFieldDateTimeInput';

const classes = {
  container: `${PREFIX}-container`,
  fields: `${PREFIX}-fields`,
  appointment: `${PREFIX}-appointment`,
  pickup: `${PREFIX}-pickup`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    [`&.${classes.pickup}`]: {
      marginLeft: theme.spacing(4),
      marginTop: theme.spacing(-1),
      marginBottom: theme.spacing(1),
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      width: '75%',
    },
  },
  [`& .${classes.fields}`]: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    '& > *': { flex: '1 1 140px', minWidth: 0 },
  },
}));

/**
 * Advisory (non-blocking) styling: outline, label and helper text in the
 * warning color, so the field draws the eye without reading as a hard error.
 */
const warningFieldSx = {
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'warning.main' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'warning.main' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'warning.main',
  },
  '& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused': {
    color: 'warning.main',
  },
  '& .MuiFormHelperText-root': { color: 'warning.main' },
} as const;

export function TripArrayFieldDateTimeInput({
  tripField,
  index,
  required,
  disabled,
  readOnly,
  minDateTime,
}: {
  /** Luxon datetime; `appointmentDate` / `pickupDate` are separate string fields synced from this control. */
  tripField: Extract<
    TripArrayFieldKey,
    'appointmentDateTime' | 'pickupDateTime'
  >;
  index: number;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  minDateTime?: DateTime | null;
}) {
  const { control, setValue, trigger, getValues } =
    useFormContext<ReservationFormInput>();
  const [timeInteracted, setTimeInteracted] = useState(false);
  const { t } = useTranslation('common');
  const isClientPortal = usePortalLabel() === 'client-portal';
  const { fieldRefs } = useReservationFormMachines();
  const actor = fieldRefs[tripField];
  useFieldActorSnapshot(actor);
  const estimatedPickupSnapshot = useFieldActorSnapshot(
    tripField === 'pickupDateTime'
      ? fieldRefs.tripLegEstimatedPickupRefs[index]
      : null,
  );
  const estimatedPickupTime = (
    (estimatedPickupSnapshot?.context ?? {
      estimatedPickupTime: null,
    }) as TripLegEstimatedPickupContext
  ).estimatedPickupTime;

  const timeZones = useAppSelector(lookupSlice.selectTimeZones);

  const pickupTimeZone = useWatch({
    control,
    name: `trips.${index}.pickupTimeZone`,
  }) as string | null | undefined;

  const pickupTimeZoneName = reservationUtils.getTimeZoneAbbrev(
    pickupTimeZone ?? '',
    timeZones,
  );

  const dateFieldLabel =
    tripField === 'appointmentDateTime'
      ? t('member.apptDate')
      : t('member.date');

  const timeFieldLabel =
    tripField === 'pickupDateTime' && pickupTimeZoneName
      ? `${t('member.time')} (${pickupTimeZoneName})`
      : t('member.time');

  const requiredFieldMessage = t('common.requiredField');
  const invalidDateMessage = t('common.invalidDate');
  const name = `trips.${index}.${tripField}` as const;
  const dateField =
    tripField === 'pickupDateTime' ? 'pickupDate' : 'appointmentDate';
  const dateFieldName = `trips.${index}.${dateField}` as const;
  const { errors: formErrors } = useFormState({
    control,
    name: dateFieldName,
  });
  const dateFieldError = formErrors.trips?.[index]?.[dateField];
  const pairedDateIso = useWatch({
    control,
    name: dateFieldName,
  }) as string | undefined;
  const pairedDateValue = toPickerDateTime(
    pairedDateIso ? DateTime.fromISO(pairedDateIso) : null,
  );
  // The appointment picker emits browser-zone values; read its wall clock in
  // the pickup zone so pickup comparisons don't shift by the viewer's offset.
  const pickupZone = tripField === 'pickupDateTime' ? pickupTimeZone : null;
  const appointmentDateTime = inZoneKeepingLocalTime(
    useWatch({
      control,
      name: `trips.${index}.appointmentDateTime`,
    }),
    pickupZone,
  );
  // Lead time before the appointment is a client-portal rule. F1 can set
  // a pickup closer than the estimated travel buffer
  const pickupOffsetMinutes = isClientPortal
    ? isFiniteOrZero(estimatedPickupTime?.totalPickupTime)
    : 0;

  const selectedDate = (fieldValue: DateTime | null | undefined) =>
    toPickerDateTime(fieldValue) ?? pairedDateValue;

  const sendChange = (next: DateTime | null) => {
    actor.send({
      type: 'CHANGE',
      index,
      value: next?.toISO() ?? '',
    });
  };

  // Appointment drives calculate-pickup-time via the estimate machine's
  // subscription to this actor. Sync on blur only so typing/clock ticks do not
  // refetch on every keystroke. Pickup still syncs on change (no estimate fetch).
  const syncMachineOnChange = tripField === 'pickupDateTime';

  // The paired date machine must track the RHF string: shouldValidateReservation
  // reads machines, so a date picked without a time would otherwise never
  // reach it and /validate stays gated off. See: F1V2-6580
  const setPairedDate = (isoDate: string) => {
    setValue(dateFieldName, isoDate, {
      shouldValidate: true,
      shouldDirty: true,
    });
    fieldRefs[dateField].send({ type: 'CHANGE', index, value: isoDate });
  };

  const syncPairedDateString = (next: DateTime | null) => {
    setPairedDate(next?.toISODate() ?? '');
  };

  const syncAppointmentMachineFromForm = () => {
    const current = getValues(name) as DateTime | null | undefined;
    sendChange(current ?? null);
  };

  /** Blur covers typing; onClose covers clock/calendar selection that may not blur. */
  const handlePickerBlur = () => {
    if (!syncMachineOnChange) {
      syncAppointmentMachineFromForm();
    }
    actor.send({ type: 'BLUR', index });
  };

  useEffect(() => {
    if (timeInteracted) {
      void trigger(name);
    }
  }, [timeInteracted, name, trigger]);

  useEffect(() => {
    if (tripField !== 'pickupDateTime' || pickupOffsetMinutes <= 0) return;
    void trigger(name);
  }, [pickupOffsetMinutes, name, trigger, tripField]);

  return (
    <Controller
      name={name}
      control={control as unknown as Control<FieldValues>}
      rules={{
        validate: (value: DateTime | null) => {
          const hasPairedDate = pairedDateValue != null;
          const hasValidDateTime = isValidDateTime(value);

          if (value && !isValidDateTime(value)) {
            return invalidDateMessage;
          }

          if (required) {
            if (!hasPairedDate && !hasValidDateTime) {
              return requiredFieldMessage;
            }
            if (hasPairedDate && !hasValidDateTime && timeInteracted) {
              return requiredFieldMessage;
            }
          }

          if (
            tripField === 'pickupDateTime' &&
            isValidDateTime(value) &&
            appointmentDateTime &&
            pickupOffsetMinutes > 0
          ) {
            const minutesUntilAppointment = appointmentDateTime.diff(
              inZoneKeepingLocalTime(value, pickupZone) ?? value,
              'minutes',
            ).minutes;
            if (minutesUntilAppointment < pickupOffsetMinutes) {
              return t('reservation.pickupTimeOffsetError', {
                minutes: pickupOffsetMinutes,
              });
            }
          }

          // The blocking field error always uses the instructive wording,
          // whether a full date+time was entered or just a date so far —
          // the plain "different than appointment date" wording is
          // reserved for the separate, non-blocking
          // PickupDateMismatchMessage.
          if (
            tripField === 'pickupDateTime' &&
            isValidDateTime(value) &&
            appointmentDateTime &&
            !isWithinPickupDateRange(value, appointmentDateTime)
          ) {
            return t('reservation.pickupDateInvalidError');
          }
          if (
            tripField === 'pickupDateTime' &&
            !isValidDateTime(value) &&
            pairedDateValue &&
            appointmentDateTime &&
            !isWithinPickupDateRange(pairedDateValue, appointmentDateTime)
          ) {
            return t('reservation.pickupDateInvalidError');
          }
          return true;
        },
      }}
      render={({ field, fieldState }) => {
        const fieldDateTime = toPickerDateTime(field.value);
        const pickupInZone = inZoneKeepingLocalTime(field.value, pickupZone);
        const effectiveSelectedDate = selectedDate(field.value);
        const pickerMinDateTime = toPickerDateTime(minDateTime);
        const pickupMaxDate =
          tripField === 'pickupDateTime' && appointmentDateTime
            ? appointmentDateTime
            : undefined;
        const pickupMinDate =
          tripField === 'pickupDateTime' && appointmentDateTime
            ? safeMinus(appointmentDateTime, { days: 1 })?.startOf('day')
            : undefined;
        const pickupMaxTime =
          tripField === 'pickupDateTime' &&
          appointmentDateTime &&
          effectiveSelectedDate?.hasSame(appointmentDateTime, 'day')
            ? safeMinus(appointmentDateTime, { minutes: pickupOffsetMinutes })
            : undefined;
        const effectiveMinTime =
          pickerMinDateTime &&
          effectiveSelectedDate?.hasSame(pickerMinDateTime, 'day')
            ? pickerMinDateTime
            : undefined;

        const helperText = fieldState.error?.message;
        const hasDateOnlySelection =
          pairedDateValue != null && fieldDateTime == null;
        const pickupOffsetError =
          tripField === 'pickupDateTime' &&
          isPickupLeadTimeTooShort(
            inZoneKeepingLocalTime(field.value, pickupZone),
            appointmentDateTime,
            pickupOffsetMinutes,
          )
            ? t('reservation.pickupTimeOffsetError', {
                minutes: pickupOffsetMinutes,
              })
            : null;
        // The out-of-range message is about the date, not the time — show
        // it under the Date control only, not duplicated under Time too.
        const isPickupDateMismatchError =
          tripField === 'pickupDateTime' &&
          helperText === t('reservation.pickupDateInvalidError');
        // A server pickUpDueDateTime error is a silent error on the date
        // string. Highlight the date control only; the time control stays
        // clear. The mismatch error is the exception: it's allowed through
        // even with only a date typed so far (no time yet), since it's
        // computed from the paired date string directly (see rules.validate
        // above) and would otherwise show no feedback at all until a time
        // is also entered.
        const datePickerError =
          (!!fieldState.error &&
            (!hasDateOnlySelection || isPickupDateMismatchError)) ||
          Boolean(dateFieldError);
        const timePickerError =
          (!!fieldState.error &&
            (!hasDateOnlySelection || timeInteracted) &&
            !isPickupDateMismatchError) ||
          !!pickupOffsetError;
        const datePickerHelperText =
          tripField === 'appointmentDateTime' || !datePickerError
            ? null
            : helperText;
        const showPickupTimeWarning =
          tripField === 'pickupDateTime' &&
          !fieldState.error &&
          !pickupOffsetError &&
          isPickupTimeFarFromAppointment(pickupInZone, appointmentDateTime);
        const pickupTimeWarningText = showPickupTimeWarning
          ? t('reservation.pickupTimeFarFromAppointmentWarning')
          : null;
        const timePickerHelperText = pickupOffsetError
          ? null
          : tripField === 'appointmentDateTime' || !timePickerError
            ? pickupTimeWarningText
            : helperText;
        const timeReferenceDate =
          effectiveSelectedDate?.startOf('day') ??
          defaultTimePickerReferenceDate();
        return (
          <StyledBox
            className={`${classes.container} ${tripField === 'appointmentDateTime' ? classes.appointment : classes.pickup}`}>
            <Box className={classes.fields}>
              <DatePicker
                label={dateFieldLabel}
                value={fieldDateTime ?? pairedDateValue}
                disabled={disabled}
                readOnly={readOnly ?? false}
                minDate={
                  tripField === 'pickupDateTime'
                    ? pickupMinDate
                    : (pickerMinDateTime ?? undefined)
                }
                maxDate={pickupMaxDate}
                onClose={() => {
                  if (!syncMachineOnChange) {
                    syncAppointmentMachineFromForm();
                  }
                }}
                slotProps={{
                  textField: {
                    variant: 'outlined',
                    required,
                    error: datePickerError,
                    helperText: datePickerHelperText,
                    FormHelperTextProps:
                      tripField === 'pickupDateTime'
                        ? ({
                            'data-testid': 'pickup-date-time-error',
                          } as Record<string, unknown> as ComponentProps<
                            typeof FormHelperText
                          >)
                        : undefined,
                    onFocus: () => actor.send({ type: 'FOCUS', index }),
                    onBlur: () => {
                      handlePickerBlur();
                      field.onBlur();
                    },
                    sx: {
                      width: '100%',
                      '& .MuiInputAdornment-root .MuiIconButton-root': {
                        padding: '4px',
                        marginLeft: '-15px',
                      },
                      '& .MuiFormLabel-root[data-shrink="false"]': {
                        maxWidth: 'calc(100% - 48px)',
                      },
                    },
                  },
                }}
                onChange={(date) => {
                  // The calendar grid still blocks clicking an out-of-range
                  // day (minDate/maxDate below), but a typed date is let
                  // through here on purpose: it needs to register in form
                  // state so the Controller's rules.validate (above) can
                  // flag it with a visible error — and reservationFormSchema
                  // blocks Next/Submit for the same reason (see
                  // reservationForm.schema.tsx).
                  setTimeInteracted(false);
                  const next = mergeDatePart(fieldDateTime, date);
                  const hasExistingDateTime = fieldDateTime != null;

                  if (hasExistingDateTime) {
                    field.onChange(next);
                    syncPairedDateString(next);
                    if (syncMachineOnChange) {
                      sendChange(next);
                    }
                    void trigger(name);
                    return;
                  }

                  // Keep date-only selection without fabricating a midnight time.
                  field.onChange(null);
                  const selectedDateOnly = toPickerDateTime(date);
                  setPairedDate(selectedDateOnly?.toISODate() ?? '');
                  if (syncMachineOnChange) {
                    sendChange(null);
                  }
                  void trigger(name);
                }}
              />
              <DesktopTimePicker
                label={timeFieldLabel}
                value={fieldDateTime}
                referenceDate={timeReferenceDate}
                disabled={disabled}
                readOnly={readOnly}
                minTime={effectiveMinTime}
                maxTime={pickupMaxTime}
                closeOnSelect={false}
                onClose={() => {
                  if (!syncMachineOnChange) {
                    syncAppointmentMachineFromForm();
                  }
                }}
                slotProps={{
                  textField: {
                    variant: 'outlined',
                    required,
                    error: timePickerError,
                    helperText: timePickerHelperText,
                    FormHelperTextProps:
                      tripField === 'pickupDateTime' && timePickerHelperText
                        ? ({
                            'data-testid': timePickerError
                              ? 'pickup-time-error'
                              : 'pickup-time-warning',
                          } as Record<string, unknown> as ComponentProps<
                            typeof FormHelperText
                          >)
                        : undefined,
                    onFocus: () => {
                      setTimeInteracted(true);
                      setValue(name, fieldDateTime, { shouldDirty: true });
                      actor.send({ type: 'FOCUS', index });
                    },
                    onBlur: () => {
                      handlePickerBlur();
                      field.onBlur();
                    },
                    sx: {
                      width: '100%',
                      ...(showPickupTimeWarning ? warningFieldSx : {}),
                    },
                  },
                }}
                onOpen={() => setTimeInteracted(true)}
                onChange={(time) => {
                  if (time !== null && !isValidDateTime(time)) {
                    return;
                  }
                  const fallbackBase = pairedDateValue?.startOf('day') ?? null;
                  const next = mergeTimePart(
                    fieldDateTime ?? fallbackBase,
                    time,
                  );
                  field.onChange(next);
                  syncPairedDateString(next ?? fallbackBase);
                  if (syncMachineOnChange) {
                    sendChange(next);
                  }
                  void trigger(name);
                }}
              />
            </Box>
            {pickupOffsetError ? (
              <FormHelperText error data-testid="pickup-time-error">
                {pickupOffsetError}
              </FormHelperText>
            ) : null}
          </StyledBox>
        );
      }}
    />
  );
}
